import { ApiQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ModuleRendering, SlotRendering, isModuleRenderingRenderable } from '@frontend/common-types';
import { DefaultPrimaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { Module } from '@frontend/module/types';
import { Spot } from '@frontend/spot/types';
import React from 'react';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { ModuleSlotList } from '../module-slot-list';
import { Module as ModuleComponent } from '../module/module.component';
import { ZoomControls } from '../zoom-controls/zoom-controls.component';
import { useSpotLayoutController } from './spot-layout.controller';

export interface SpotLayoutStyles<TSlot extends SlotRendering = SlotRendering> {
    getSlotColor?: (slot: TSlot) => string;
}

export type RenderMode = 'spot' | 'module';

interface SpotLayoutProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    spot?: Spot;
    module?: Module;
    onSlotClick?: (clickedSlot: TSlot) => void;
    onModuleClick?: (module: TModule) => void;
    onModuleNameClick?: () => void;
    onSelectedModuleChange?: (moduleId: string) => void;
    fetchSpotData: (arg?: ApiQueryParams<string | number>) => Promise<PaginatedResponse<TModule>>;
    fetchModuleData: (accountId: string, spotId: string, moduleId: string) => Promise<{ results: TSlot[] }>;
    showLabels?: boolean;
    showLegend?: boolean;
    enableZoom?: boolean;
    enableZoomControls?: boolean;
    enableSlotHover?: boolean;
    className?: string;
    renderMode?: RenderMode;
    centered?: boolean;
    minScale?: number;
    maxScale?: number;
    styles?: SpotLayoutStyles<TSlot>;
    slot?: {
        hoverElement?: (slot: TSlot) => React.ReactNode;
        renderElement?: (slot: TSlot) => React.ReactNode;
    };
    /**
     * When true, disables the ability to click on module labels to expand/focus on a single module.
     * @default false
     */
    disableModuleExpand?: boolean;
}

export const SpotLayout = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(
    props: SpotLayoutProps<TModule, TSlot>
) => {
    const {
        containerRef,
        initialLoad,
        data,
        selectedModuleId,
        modules,
        modulesWithSlots,
        currentModule,
        isFirstModule,
        isLastModule,
        scale,
        centerOffsetX,
        centerOffsetY,
        containerWidth,
        containerHeight,
        handleModuleExpand,
        handleNavigateModule,
        renderMode
    } = useSpotLayoutController<TModule, TSlot>({
        spot: props.spot,
        module: props.module,
        renderMode: props.renderMode,
        fetchSpotData: props.fetchSpotData,
        fetchModuleData: props.fetchModuleData,
        onSelectedModuleChange: props.onSelectedModuleChange,
        minScale: props.minScale,
        maxScale: props.maxScale
    });

    if (initialLoad) {
        return (
            <div className={`flex h-full items-center justify-center ${props.className}`}>
                <Spinner />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className={`flex h-full items-center justify-center ${props.className}`}>
                <p className='text-brand-muted'>
                    <FormattedMessage
                        id='kiosk.spot-rendering.spot-layout.no-layout-data'
                        defaultMessage='No distriSPOT layout data available.'
                        description='Empty state when the kiosk has no layout data to render for the current distriSPOT'
                    />
                </p>
            </div>
        );
    }

    const nonRenderableModules = modulesWithSlots.filter((module) => !isModuleRenderingRenderable(module));
    const renderableModules = modulesWithSlots.filter((module) => isModuleRenderingRenderable(module));
    const showSlotListOnly = modulesWithSlots.length > 0 && renderableModules.length === 0;

    const layoutContent = (
        <>
            {renderableModules.length > 0 ? (
                <div
                    className={selectedModuleId || props.centered ? 'relative mx-auto' : 'relative'}
                    style={{
                        width: `${containerWidth}px`,
                        height: `${containerHeight}px`
                    }}>
                    {renderableModules.map((module) => (
                        <ModuleComponent
                            key={module.id}
                            module={module}
                            scale={scale}
                            offsetX={centerOffsetX}
                            offsetY={centerOffsetY}
                            onSlotClick={props.onSlotClick}
                            onModuleClick={props.onModuleClick}
                            showLabels={props.showLabels}
                            spotId={props.spot?.id}
                            onModuleExpand={props.disableModuleExpand ? undefined : handleModuleExpand}
                            // Module mode: never force geometric expand; non-renderable uses ModuleSlotList instead.
                            // Spot overview still uses isExpanded to collapse chrome (e.g. carousel → slots).
                            isExpanded={renderMode === 'module' ? false : selectedModuleId === module.id}
                            enableSlotHover={props.enableSlotHover}
                            slotHoverElement={props.slot?.hoverElement}
                            getSlotColor={props.styles?.getSlotColor}
                            slotRenderElement={props.slot?.renderElement}
                        />
                    ))}
                </div>
            ) : null}
            {nonRenderableModules.length > 0 ? (
                <ModuleSlotList
                    modules={nonRenderableModules}
                    onSlotClick={props.onSlotClick}
                    slotRenderElement={props.slot?.renderElement}
                />
            ) : null}
        </>
    );

    return (
        <div className={`flex h-full w-full flex-col gap-4 ${props.className}`}>
            {props.spot && selectedModuleId && props.renderMode !== 'module' && (
                <div className='border-border-default flex items-center justify-between border-t border-solid p-4'>
                    <div className='flex w-full items-center gap-4'>
                        <DefaultPrimaryButton
                            text='Go back'
                            onClick={() => handleModuleExpand(props.spot!.id, selectedModuleId)}
                        />
                        <div className='text-foreground-default flex w-full gap-2 text-xl font-semibold'>
                            <p>{props.spot.name}</p>
                            <p>/</p>
                            <p>{currentModule?.index}</p>
                        </div>
                    </div>
                </div>
            )}
            {props.renderMode === 'module' && selectedModuleId && modules.length > 1 && (
                <div className='flex items-center justify-center gap-3'>
                    <button
                        onClick={() => handleNavigateModule('prev')}
                        disabled={isFirstModule}
                        className='border-border-default bg-background-surface flex size-10 items-center justify-center rounded-full border border-solid disabled:cursor-not-allowed disabled:opacity-50'
                        aria-label='Previous module'>
                        <LuArrowLeft className='text-foreground-default size-5' />
                    </button>
                    <div
                        onClick={props.onModuleNameClick}
                        className='border-border-default bg-background-surface flex h-10 items-center justify-center rounded-xl border border-solid px-4'>
                        <span className='text-foreground-default text-xl font-semibold'>
                            {CommonMessage.OBJECTS.MODULE.DEFAULT} {currentModule?.index ?? ''}
                        </span>
                    </div>
                    <button
                        onClick={() => handleNavigateModule('next')}
                        disabled={isLastModule}
                        className='border-border-default bg-background-surface flex size-10 items-center justify-center rounded-full border border-solid disabled:cursor-not-allowed disabled:opacity-50'
                        aria-label='Next module'>
                        <LuArrowRight className='text-foreground-default size-5' />
                    </button>
                </div>
            )}
            <div className='relative flex flex-1 flex-col'>
                {props.enableZoom && props.enableZoomControls && !showSlotListOnly ? (
                    <TransformWrapper
                        centerOnInit={false}
                        initialScale={1}
                        minScale={0.5}
                        maxScale={3}
                        wheel={{ step: 0.1 }}
                        panning={{ disabled: false }}
                        disablePadding={true}>
                        <>
                            <div
                                ref={containerRef}
                                className={`flex-1 ${props.renderMode === 'module' ? 'overflow-hidden' : 'overflow-auto'} rounded-lg p-4 ${selectedModuleId ? 'flex items-center justify-center' : ''}`}>
                                <TransformComponent wrapperClass='w-full h-full'>{layoutContent}</TransformComponent>
                            </div>
                            <div className='border-border-default bg-background-surface border-t p-5'>
                                <div className='flex items-center justify-between'>
                                    <span className='text-foreground-default text-2xl font-semibold'>{props.spot?.name}</span>
                                    <ZoomControls />
                                </div>
                            </div>
                        </>
                    </TransformWrapper>
                ) : (
                    <div
                        ref={containerRef}
                        className={`flex-1 rounded-lg p-4 ${
                            showSlotListOnly
                                ? 'w-full overflow-y-auto'
                                : `${props.renderMode === 'module' ? 'overflow-hidden' : 'overflow-auto'} ${selectedModuleId || props.centered ? 'flex items-center justify-center' : ''}`
                        }`}>
                        {layoutContent}
                    </div>
                )}
            </div>
        </div>
    );
};
